import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { TooltipProvider } from '@/components/ui/tooltip';
import { TutorialModeProvider } from '@/contexts/TutorialModeContext';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { store } from '@/store';
import { setupApiErrorHandler } from '@/lib/api-common/setupApiErrorHandler';
import { AuthInit } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import { PageLoader } from '@/components/Spinner';
import '@/lib/i18n';
import { updateFavicon } from '@/lib/utils/favicon';
import { initSocket } from '@/lib/socket';
import { detectRuntimeEnvFromBrowser, detectBeTargetEnvFromBrowser } from '@/lib/config/runtimeEnv';

setupApiErrorHandler(store);
updateFavicon();
initSocket();
document.documentElement.dataset.env = detectRuntimeEnvFromBrowser();
document.documentElement.dataset.beEnv = detectBeTargetEnvFromBrowser();

// Force date inputs to display in YYYY-MM-DD (ISO 8601) format.
// The HTML lang attribute on <html> controls Chrome's date input format.
// en-CA (Canadian English) uses ISO 8601 — does NOT affect app UI language
// which is managed by the i18n system independently.
document.documentElement.lang = 'en-CA';
// App shell (header/footer/global overlays) stays eager — needed on every route.
import App from '@/App';

/**
 * 페이지 컴포넌트는 전부 React.lazy로 라우트 단위 code-splitting.
 * 이전에는 아래 컴포넌트 전부가 main.tsx에서 즉시 import돼 하나의 진입 번들에
 * 묶였다(어떤 라우트를 방문하든 전체 어드민 앱 코드를 다운로드). 각 페이지를
 * 별도 청크로 분리해 방문한 라우트의 코드만 받도록 한다.
 * (Suspense fallback은 `<PageLoader />` — 앱 전역에서 이미 쓰는 페이지 로딩 컴포넌트.)
 */
const Login = lazy(() => import('@/pages/auth/login'));
const ForgotPassword = lazy(() => import('@/pages/auth/forgot-password'));
const ResetPassword = lazy(() => import('@/pages/auth/reset-password'));
const ChangePassword = lazy(() => import('@/pages/auth/change-password'));
const Home = lazy(() => import('@/pages/home'));
const GgcComponentsDemo = lazy(() => import('@/pages/_dev/ggc-components'));
const GgcCommonComponentsDemo = lazy(() => import('@/pages/_dev/ggc-common-components'));
const InfluencerList = lazy(() => import('@/pages/influencers/index'));
const Done = lazy(() => import('@/pages/done'));
const CampaignsList = lazy(() => import('@/pages/campaigns/index'));
const CampaignDetails = lazy(() => import('@/pages/campaigns/details'));
const CampaignsEditor = lazy(() => import('@/pages/campaigns/editor'));
const AdvertiserPage = lazy(() => import('@/pages/campaigns/advertiser'));
const CampaignsResults = lazy(() => import('@/pages/campaigns/results'));
const CampaignSummary = lazy(() => import('@/pages/campaigns/summary'));
const CampaignPostCandidates = lazy(() => import('@/pages/campaigns/post-candidates'));
const Analytics = lazy(() => import('@/pages/stats/analytics'));
const DedicatedStats = lazy(() => import('@/pages/stats/dedicated'));
const Stats = lazy(() => import('@/pages/stats/campaign-analytics'));
const Blacklist = lazy(() => import('@/pages/blacklist'));
const Candidates = lazy(() => import('@/pages/candidates'));
const CommunicationsHistory = lazy(() => import('@/pages/communications'));
const Payments = lazy(() => import('@/pages/payments/index'));
const PaymentsReview = lazy(() => import('@/pages/payments/review'));
const Finances = lazy(() => import('@/pages/payments/finances'));
const Templates = lazy(() => import('@/pages/templates'));
const Admins = lazy(() => import('@/pages/admins'));
const AdvertisersEditor = lazy(() => import('@/pages/advertisers'));
const UsersProfiles = lazy(() => import('@/pages/influencers/profile'));
const UserPanel = lazy(() => import('@/pages/panel/user'));
const Invitations = lazy(() => import('@/pages/invitations'));
const Platform = lazy(() => import('@/pages/platform/index'));
const CrawlerPage = lazy(() => import('@/pages/influencers/add'));
const Posts = lazy(() => import('@/pages/videos'));
const FeatureUnavailable = lazy(() => import('@/pages/feature-unavailable'));
const Settings = lazy(() => import('@/pages/settings'));
const ShareLinks = lazy(() => import('@/pages/share-links'));
const ImportInfluencers = lazy(() => import('@/pages/influencers/import'));
const GeneralPosts = lazy(() => import('@/pages/influencers/general-posts'));
const RostersList = lazy(() => import('@/pages/rosters/index'));
const RosterDetail = lazy(() => import('@/pages/rosters/detail'));
// Named export (shares the same `rosters/detail` chunk as RosterDetail above).
const RosterImportHistoryPage = lazy(() =>
  import('@/pages/rosters/detail').then((m) => ({ default: m.RosterImportHistoryPage }))
);
const SitemapPage = lazy(() => import('@/pages/sitemap'));
const GgcTagsPage = lazy(() => import('@/pages/tools/ggc-tags'));
import '@/index.css';

/**
 * 래퍼 컴포넌트: 인증 필요 페이지 (admin + guest)
 * BE의 passportConfig.isAuthenticated([], ['admin', 'guest']) 와 동일
 */
function Auth({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function RedirectToStatsCampaign() {
  const { campaignId, token } = useParams<{ campaignId?: string; token?: string }>();
  const to = token ? `/stats/campaign/${campaignId}/${token}` : `/stats/campaign/${campaignId}`;
  return <Navigate to={to} replace />;
}

// Legacy alias: the campaign editor route was renamed from /campaigns/editV2/*
// to /campaigns/edit/*. Keep this redirect so old links/bookmarks still resolve.
function RedirectEditV2ToEdit() {
  const { campaignId, tab } = useParams<{ campaignId?: string; tab?: string }>();
  const to = `/campaigns/edit/${campaignId}${tab ? `/${tab}` : ''}`;
  return <Navigate to={to} replace />;
}

// Legacy alias: v1's influencer dashboard was `/dashboard`, renamed to
// `/influencers` on v2. Must forward the query string (e.g. the Tampermonkey
// crawler's `?filter-channelId=` deep link, see src/tampermonkey/widget.ts) —
// a plain `<Navigate to="/influencers">` drops it, silently breaking the
// "view this channel" link even though /influencers itself supports the param.
function RedirectDashboardToInfluencers() {
  const { search } = useLocation();
  return <Navigate to={`/influencers${search}`} replace />;
}

/**
 * 래퍼 컴포넌트: admin 전용 + toAccessAllFeatures 권한 필요
 * BE의 passportConfig.isAuthenticated(['toAccessAllFeatures'], ['admin', 'guest']) 와 동일
 */
function AuthFull({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute requireAccessAllFeatures>{children}</ProtectedRoute>;
}

/** admin 역할(role === 'admin')만 접근 가능 */
function AuthAdminOnly({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute adminOnly>{children}</ProtectedRoute>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <TutorialModeProvider>
    <TooltipProvider>
    <Provider store={store}>
      <AuthInit>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<App />}>
              {/* === 비인증 접근 가능 === */}
              <Route index element={<Home />} />
              <Route path="login" element={<Login />} />
              <Route path="login/:token" element={<Login />} />
              <Route path="forgot_password" element={<ForgotPassword />} />
              <Route path="reset_password/:token" element={<ResetPassword />} />
              <Route path="change_password" element={<ChangePassword />} />
              <Route path="done" element={<Done />} />

              {/* === 인증 필요 (admin + guest) === */}
              <Route path="influencers" element={<Auth><InfluencerList /></Auth>} />
              <Route path="dashboard" element={<RedirectDashboardToInfluencers />} />
              <Route path="feature-unavailable" element={<Auth><FeatureUnavailable /></Auth>} />
              <Route path="dashboard/campaign/:campaignId" element={<Auth><InfluencerList /></Auth>} />
              <Route path="campaigns" element={<Auth><CampaignsList /></Auth>} />
              <Route path="campaigns/details" element={<Auth><CampaignDetails /></Auth>} />
              <Route path="campaigns/details/:campaignId" element={<Auth><CampaignDetails /></Auth>} />
              <Route path="campaigns/edit/:campaignId" element={<Auth><CampaignsEditor /></Auth>} />
              <Route path="campaigns/edit/:campaignId/:tab" element={<Auth><CampaignsEditor /></Auth>} />
              {/* Legacy alias — /campaigns/editV2/* → /campaigns/edit/* (renamed) */}
              <Route path="campaigns/editV2/:campaignId" element={<RedirectEditV2ToEdit />} />
              <Route path="campaigns/editV2/:campaignId/:tab" element={<RedirectEditV2ToEdit />} />
              <Route path="campaigns/advertiser/:campaignId" element={<Auth><AdvertiserPage /></Auth>} />
              <Route path="campaigns/advertiser/:campaignId/:tab" element={<Auth><AdvertiserPage /></Auth>} />
              <Route path="campaigns/results" element={<Auth><CampaignsResults /></Auth>} />
              <Route path="summary" element={<Auth><CampaignSummary /></Auth>} />
              <Route path="summary/:campaignId" element={<Auth><CampaignSummary /></Auth>} />
              <Route path="campaign_post_candidates/:campaignId" element={<Auth><CampaignPostCandidates /></Auth>} />
              <Route path="analytics" element={<Auth><Analytics /></Auth>} />
              <Route path="stats" element={<Auth><Analytics /></Auth>} />
              <Route path="analytics/campaign/:campaignId" element={<RedirectToStatsCampaign />} />
              <Route path="analytics/campaign/:campaignId/:token" element={<RedirectToStatsCampaign />} />
              <Route path="stats/campaign/:campaignId" element={<Auth><Stats /></Auth>} />
              <Route path="stats/campaign/:campaignId/:token" element={<Auth><Stats /></Auth>} />
              <Route path="stats/dedicated" element={<Auth><DedicatedStats /></Auth>} />
              <Route path="stats/dedicated/:campaignId" element={<Auth><DedicatedStats /></Auth>} />                           
              <Route path="blacklist" element={<Auth><Blacklist /></Auth>} />
              <Route path="candidates" element={<Auth><Candidates /></Auth>} />
              <Route path="candidates/:campaignId" element={<Auth><Candidates /></Auth>} />
              <Route path="communications" element={<Auth><CommunicationsHistory /></Auth>} />
              <Route path="communications/:campaignId" element={<Auth><CommunicationsHistory /></Auth>} />
              <Route path="payments" element={<Auth><Payments /></Auth>} />
              <Route path="payments/reviews" element={<Auth><PaymentsReview /></Auth>} />
              <Route path="payments/reviews/:campaignId" element={<Auth><PaymentsReview /></Auth>} />
              <Route path="templates" element={<Auth><Templates /></Auth>} />
              <Route path="templates/:templateId" element={<Auth><Templates /></Auth>} />
              <Route path="admins" element={<Auth><Admins /></Auth>} />
              <Route path="admins/:adminId" element={<Auth><Admins /></Auth>} />
              <Route path="advertisers" element={<Auth><AdvertisersEditor /></Auth>} />
              <Route path="advertisers/*" element={<Auth><AdvertisersEditor /></Auth>} />
              <Route path="influencer/:userId" element={<Auth><UsersProfiles /></Auth>} />
              <Route path="influencer/:userId/campaigns" element={<Auth><UsersProfiles /></Auth>} />
              <Route path="influencer/:userId/campaigns/:campaignId" element={<Auth><UsersProfiles /></Auth>} />
              <Route path="influencer/:userId/stats" element={<Auth><UsersProfiles /></Auth>} />
              <Route path="influencer/:userId/stats/:action" element={<Auth><UsersProfiles /></Auth>} />
              <Route path="influencer/:userId/campaigns/:campaignId/:action" element={<Auth><UsersProfiles /></Auth>} />
              <Route path="general/:userId" element={<Auth><GeneralPosts /></Auth>} />
              <Route path="influencers/add" element={<Auth><CrawlerPage /></Auth>} />
              <Route path="mailings" element={<Auth><Invitations /></Auth>} />
              <Route path="mailings/:invitationId" element={<Auth><Invitations /></Auth>} />
              <Route path="platform" element={<Auth><Platform /></Auth>} />
              <Route path="panel/user/:userId" element={<Auth><UserPanel /></Auth>} />
              <Route path="panel/user/:userId/:tab" element={<Auth><UserPanel /></Auth>} />              
              {/* no-v2 (no backend endpoint) — feature removed, redirect to notice */}
              <Route path="recommended/channels/:campaignId" element={<Navigate to="/feature-unavailable" replace />} />
              <Route path="recommended/scores" element={<Navigate to="/feature-unavailable" replace />} />
              <Route path="tasks" element={<Navigate to="/feature-unavailable" replace />} />
              <Route path="settings" element={<Auth><Settings /></Auth>} />
              <Route path="share_links" element={<Auth><ShareLinks /></Auth>} />
              <Route path="import_influencers" element={<Auth><ImportInfluencers /></Auth>} />
              <Route path="import_influencers/:tab" element={<Auth><ImportInfluencers /></Auth>} />
              <Route path="rosters" element={<Auth><RostersList /></Auth>} />
              <Route path="rosters/:rosterId" element={<Auth><RosterDetail /></Auth>} />
              <Route path="rosters/:rosterId/import-history" element={<Auth><RosterImportHistoryPage /></Auth>} />

              <Route path="invitations" element={<Auth><Invitations /></Auth>} />
              <Route path="invitations/:invitationId" element={<Auth><Invitations /></Auth>} />
              <Route path="search" element={<Navigate to="/feature-unavailable" replace />} />
              <Route path="search/game" element={<Navigate to="/feature-unavailable" replace />} />
              <Route path="advanced_search" element={<Navigate to="/feature-unavailable" replace />} />
              <Route path="twitch/games" element={<Navigate to="/feature-unavailable" replace />} />
              <Route path="videos" element={<Auth><Posts /></Auth>} />

              {/* === toAccessAllFeatures 권한 필요 === */}
              <Route path="finances" element={<AuthFull><Finances /></AuthFull>} />
              <Route path="finances/:campaignId" element={<AuthFull><Finances /></AuthFull>} />

              {/* Hidden — not linked from any menu, admin-only, noindex. */}
              <Route path="_internal/sitemap" element={<AuthAdminOnly><SitemapPage /></AuthAdminOnly>} />
              {/* === 개발 전용 — Figma 디자인 시스템 데모 === */}
              <Route path="_dev/ggc-components" element={<GgcComponentsDemo />} />
              <Route path="_dev/ggc-common-components" element={<GgcCommonComponentsDemo />} />
              {/* === 내부 도구 === */}
              <Route path="ggc-tags" element={<Auth><GgcTagsPage /></Auth>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthInit>
    </Provider>
    </TooltipProvider>
    </TutorialModeProvider>
  </React.StrictMode>
);
